/**
 * Product page enhancement.
 *
 * The form underneath is a real `<form action="/cart/add" method="post">`, so a shopper
 * with no JavaScript can still choose options and add to cart via a normal submission.
 * Everything here is an upgrade on top of that:
 *
 *   - swapping the hidden variant id, price and availability as options change
 *   - marking option values that no combination can reach
 *   - gallery thumbnails
 *   - quantity stepper
 *   - Ajax add-to-cart so the page does not reload
 *
 * If this file fails to load, every one of those degrades to the plain form.
 */
(() => {
  const init = (root) => {
    if (root.dataset.ebPdpBound === 'true') return;
    root.dataset.ebPdpBound = 'true';

    const form = root.querySelector('[data-eb-pdp-form]');
    const dataEl = root.querySelector('[data-eb-pdp-variants]');
    if (!form || !dataEl) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch {
      return; // Malformed payload: leave the native form completely alone.
    }

    const idField = form.querySelector('[data-eb-pdp-id]');
    const priceEl = root.querySelector('[data-eb-pdp-price]');
    const submit = form.querySelector('[data-eb-pdp-submit]');
    const errorEl = form.querySelector('[data-eb-pdp-error]');
    const mainImg = root.querySelector('[data-eb-pdp-main] img');

    const chosen = () =>
      Array.from(form.querySelectorAll('[data-eb-pdp-option]')).map((fs) => {
        const checked = fs.querySelector('[data-eb-pdp-value]:checked');
        return checked ? checked.value : null;
      });

    const match = (opts) =>
      variants.find((v) => opts.every((o, i) => o === null || v.options[i] === o));

    /** Grey out values that cannot be reached from the current selection. */
    const markUnavailable = () => {
      const sel = chosen();
      for (const [i, fs] of Array.from(form.querySelectorAll('[data-eb-pdp-option]')).entries()) {
        for (const input of fs.querySelectorAll('[data-eb-pdp-value]')) {
          const probe = sel.slice();
          probe[i] = input.value;
          const found = variants.find((v) => probe.every((o, k) => o === null || v.options[k] === o));
          const label = input.nextElementSibling;
          if (label) label.dataset.unavailable = String(!found || !found.available);
        }
      }
    };

    const sync = () => {
      const variant = match(chosen());
      markUnavailable();

      if (!variant) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = submit.dataset.soldOut || 'Unavailable';
        }
        return;
      }

      if (idField) idField.value = variant.id;
      if (priceEl) {
        priceEl.innerHTML =
          variant.price + (variant.onSale > 0 ? ` <s class="eb-pdp__was">${variant.compare}</s>` : '');
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available
          ? submit.dataset.addLabel || submit.textContent
          : submit.dataset.soldOut || 'Sold out';
      }
      if (mainImg && variant.image) mainImg.src = variant.image;

      // Keep the URL shareable — a copied link reopens on the same variant.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    // Remember the initial labels so availability changes can restore them.
    if (submit) {
      submit.dataset.addLabel = submit.textContent.trim();
      submit.dataset.soldOut = submit.dataset.soldOut || 'Sold out';
    }

    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-eb-pdp-value]')) sync();
    });

    // Quantity stepper
    const qty = form.querySelector('[data-eb-qty-input]');
    for (const btn of form.querySelectorAll('[data-eb-qty]')) {
      btn.addEventListener('click', () => {
        if (!qty) return;
        const next = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(btn.dataset.ebQty));
        qty.value = String(next);
      });
    }

    // Gallery thumbnails
    for (const thumb of root.querySelectorAll('[data-eb-pdp-thumb]')) {
      thumb.addEventListener('click', () => {
        if (mainImg && thumb.dataset.full) mainImg.src = thumb.dataset.full;
        for (const t of root.querySelectorAll('[data-eb-pdp-thumb]')) t.classList.toggle('is-active', t === thumb);
      });
    }

    // Ajax add-to-cart. Falls back to a normal submit on any failure.
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (errorEl) errorEl.hidden = true;

      const original = submit ? submit.textContent : '';
      if (submit) {
        submit.disabled = true;
        submit.textContent = '…';
      }

      try {
        const body = new FormData(form);
        const res = await fetch(`${Theme.routes.cart_add_url}`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const json = await res.json();
        if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (submit) submit.textContent = 'Added';
        setTimeout(() => {
          if (submit) {
            submit.textContent = original;
            submit.disabled = false;
          }
        }, 1400);
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
        }
        if (submit) {
          submit.textContent = original;
          submit.disabled = false;
        }
      }
    });

    sync();
  };

  const boot = () => {
    for (const root of document.querySelectorAll('.eb-pdp')) init(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
